import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';

// project hooks
import useAuth from 'hooks/useAuth';
import { CircularProgress, Stack } from '@mui/material';
import { verifyToken } from 'contexts/auth/session';

// ==============================|| AUTH GUARD ||============================== //

/**
 * Authentication guard for routes
 * @param {PropTypes.node} children children element/node
 */
const AuthGuard = ({ children, routes = [] }) => {
    const [isLoading, setIsLoading] = useState(true);

    const { isLoggedIn, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const routeAuthorizationRules = useMemo(() => {
        const matches = matchRoutes(routes, location) || [];

        return matches.reduce(
            (acc, match) => {
                acc.requiredAuthorizations.push(...(match.route.requiredAuthorizations || []));

                if (match.route.requiredAuthorizationPattern) {
                    acc.requiredAuthorizationPatterns.push(match.route.requiredAuthorizationPattern);
                }

                return acc;
            },
            {
                requiredAuthorizations: [],
                requiredAuthorizationPatterns: []
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routes, location.pathname]);

    const userHasAccess = useMemo(
        () =>
            !(routeAuthorizationRules.requiredAuthorizations.length || routeAuthorizationRules.requiredAuthorizationPatterns.length) ||
            (routeAuthorizationRules.requiredAuthorizations.length &&
                routeAuthorizationRules.requiredAuthorizations.some((authorization) =>
                    (user?.authorizations || []).includes(authorization)
                )) ||
            (routeAuthorizationRules.requiredAuthorizationPatterns.length &&
                routeAuthorizationRules.requiredAuthorizationPatterns.some((pattern) =>
                    (user?.authorizations || []).some((authorization) => pattern.test(authorization))
                )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [routeAuthorizationRules, user?.authorizations]
    );

    useEffect(() => {
        let isMounted = true;

        (async () => {
            if (!isLoggedIn) {
                const resVerifyToken = await verifyToken(localStorage.getItem('serviceToken'));

                if (!isMounted) return;
                if (!resVerifyToken) {
                    navigate('/login', { replace: true });
                    return;
                }
            }

            if (!userHasAccess) {
                navigate('/dashboard/default', { replace: true });
                return;
            }

            setIsLoading(false);
        })();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, userHasAccess]);

    if (isLoading) {
        return (
            <Stack sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Stack>
        );
    }

    return children;
};

AuthGuard.propTypes = {
    children: PropTypes.node,
    routes: PropTypes.arrayOf(PropTypes.object)
};

export default AuthGuard;
